import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { ArtistCard } from "../components/site/ArtistCard";
import { Lightbox } from "../components/site/Lightbox";

export default function StyleDetail() {
  const { slug } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const { data: allStyles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data, isLoading, isError } = useQuery({ queryKey: ["style", slug], queryFn: async () => (await api.get(`/styles/${slug}`)).data, retry: false });
  const styleMap = Object.fromEntries(allStyles.map((s) => [s.slug, s.name]));

  if (isLoading) return <div className="pt-44 text-center text-[#828290] font-mono text-sm">Loading…</div>;
  if (isError || !data) return (
    <div className="pt-44 pb-32 text-center px-6">
      <h1 className="display text-5xl text-white mb-4">Style not found.</h1>
      <Link to="/styles" className="text-ember font-mono text-xs uppercase tracking-[0.2em] link-underline">All styles</Link>
    </div>
  );

  const { style, artists, portfolio } = data;

  return (
    <>
      <Seo title={`${style.name} Tattoos in Springfield, VA`} description={`${style.name} tattoos at Obsidian Art & Tattoo Studio, Springfield VA. ${style.blurb} Meet the artists and book your session.`} />

      <section className="pt-36 lg:pt-44 pb-16 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 border-b border-ash">
        <Reveal>
          <Link to="/styles" className="inline-flex items-center gap-2 text-[#828290] font-mono text-[0.7rem] uppercase tracking-[0.2em] mb-8 hover:text-white transition-colors"><ArrowLeft size={14} /> All styles</Link>
        </Reveal>
        <Reveal delay={60}><h1 className="display text-6xl sm:text-7xl lg:text-8xl text-white">{style.name}.</h1></Reveal>
        <Reveal delay={120}><p className="mt-8 max-w-2xl font-serif text-2xl lg:text-3xl text-[#C4C4CC] leading-snug">{style.blurb}</p></Reveal>
      </section>

      {portfolio.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20 border-b border-ash">
          <p className="eyebrow mb-10">In this style</p>
          <div className="columns-2 lg:columns-3 gap-4 lg:gap-6 [column-fill:_balance]">
            {portfolio.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 50} className="mb-4 lg:mb-6 break-inside-avoid">
                <button onClick={() => setLightbox(fileUrl(p.image))} className="mono-hover img-zoom block w-full overflow-hidden border border-ash">
                  <img src={fileUrl(p.image)} alt={`${style.name} tattoo on ${p.placement}`} loading="lazy" className="w-full object-cover" />
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-20">
        <div className="flex items-end justify-between mb-12">
          <h2 className="display text-5xl lg:text-6xl text-white">Artists in {style.name}.</h2>
        </div>
        {artists.length === 0 ? (
          <p className="text-[#9E9EA8]">No artist currently tagged for this style — start a custom request and we'll match you.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-8">
            {artists.map((a, i) => <ArtistCard key={a.slug} artist={a} index={i} styleMap={styleMap} />)}
          </div>
        )}
        <div className="mt-16 text-center">
          <Link to={`/booking?style=${style.slug}`} className="inline-flex items-center gap-3 bg-ember text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-semibold px-10 py-5 hover:bg-white transition-colors">Book {style.name} <ArrowRight size={15} /></Link>
        </div>
      </section>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
